import { ApiService } from './apiService';

// User interface to match what components expect
export interface User {
  uid: string;
  id?: string; // Some places use 'id' instead of 'uid'
  email: string | null;
  displayName?: string | null;
  username?: string;
  emailVerified?: boolean;
  email_verified?: boolean; // Backend uses email_verified
}

let currentUser: User | null = null;

// Helper to update current user state
const updateCurrentUser = (user: User | null) => {
  currentUser = user;
};

// Check auth token and update current user
const checkAuthState = async () => {
  if (ApiService.isAuthenticated()) {
    try {
      const result = await ApiService.getCurrentUser();
      if (result.user) {
        const user: User = {
          uid: result.user.id || result.user.user_id || '',
          id: result.user.id || result.user.user_id || '',
          email: result.user.email || null,
          displayName: result.user.username || null,
          username: result.user.username || null,
          emailVerified: result.user.email_verified || result.user.emailVerified || false,
          email_verified: result.user.email_verified || result.user.emailVerified || false,
        };
        updateCurrentUser(user);
        return user;
      }
    } catch (error) {
      console.error('Error checking auth state:', error);
    }
  }
  updateCurrentUser(null);
  return null;
};

// Initialize auth state check on module load
checkAuthState();

export class AuthService {
  // Sign up with email and password
  static async signUp(email: string, password: string, username?: string) {
    try {
      const result = await ApiService.signUp(email, password, username);
      if (result.error) {
        return { user: null, error: result.error, verificationSent: false };
      }
      
      // After successful signup, check auth state
      if (result.token) {
        await checkAuthState();
      }
      
      // Note: Backend handles email verification differently
      // For now, we'll return success - backend can implement email verification later
      return { user: currentUser, error: null, verificationSent: true };
    } catch (error: any) {
      return { user: null, error: error.message, verificationSent: false };
    }
  }

  // Check username availability
  static async checkUsername(username: string): Promise<{ available: boolean; error: string | null }> {
    try {
      const result = await ApiService.checkUsername(username);
      return { available: result.available, error: result.error };
    } catch (error: any) {
      return { available: false, error: error.message };
    }
  }

  // Sign in with email and password
  static async signIn(email: string, password: string) {
    try {
      const result = await ApiService.signIn(email, password);
      if (result.error) {
        return { user: null, error: result.error, needsVerification: false };
      }
      
      // Update auth state
      if (result.token) {
        await checkAuthState();
        // Update online status
        await ApiService.updateOnlineStatus(true);
      }
      
      return { user: currentUser, error: null, needsVerification: false };
    } catch (error: any) {
      return { user: null, error: error.message, needsVerification: false };
    }
  }

  // Send verification email (kept for compatibility, but backend handles this)
  static async sendVerificationEmail(user: User) {
    // Backend handles email verification
    // This is kept for compatibility but may not be needed
    return { success: true, error: null };
  }

  // Resend verification email (kept for compatibility)
  static async resendVerificationEmail(email: string, password: string) {
    // Backend handles email verification
    return { success: true, error: null };
  }

  // Reload user to check verification (kept for compatibility)
  static async reloadUser() {
    try {
      await checkAuthState();
      return { emailVerified: currentUser?.emailVerified || false, error: null };
    } catch (error: any) {
      return { emailVerified: false, error: error.message };
    }
  }

  // Send password reset email
  static async sendPasswordReset(email: string) {
    try {
      const result = await ApiService.requestPasswordReset(email);
      return { 
        success: result.success, 
        message: result.message,
        error: result.error 
      };
    } catch (error: any) {
      return { 
        success: false, 
        message: '',
        error: error.message || 'Failed to send password reset email' 
      };
    }
  }

  // Reset password with token
  static async resetPassword(token: string, newPassword: string) {
    try {
      const result = await ApiService.resetPassword(token, newPassword);
      return { 
        success: result.success, 
        message: result.message,
        error: result.error 
      };
    } catch (error: any) {
      return { 
        success: false, 
        message: '',
        error: error.message || 'Failed to reset password' 
      };
    }
  }

  // Check if a user needs to set a custom username
  static async checkIfUserNeedsUsername(userId: string): Promise<boolean> {
    try {
      const userData = await ApiService.getUserData(userId);
      if (!userData) return true;
      // Check if username is missing or is the default placeholder
      return !userData.username || userData.username === 'User';
    } catch (error) {
      return true;
    }
  }

  // Update username for a user
  static async updateUserUsername(userId: string, username: string) {
    try {
      const result = await ApiService.updateUsername(username);
      if (result.success) {
        // Refresh auth state to get updated username
        await checkAuthState();
        return { success: true, error: null };
      }
      return { success: false, error: result.error };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Sign in with Google
  static async signInWithGoogle() {
    try {
      console.log('🔵 Starting Google sign-in...');
      
      // This will be called from AuthModal with the Google token
      // For now, return an error indicating the token is needed
      // The actual OAuth flow will be handled in AuthModal using @react-oauth/google
      return { user: null, error: 'Google sign-in should be handled via OAuth component' };
    } catch (error: any) {
      console.error('❌ Google sign-in error:', error);
      return { user: null, error: error.message };
    }
  }

  // Sign in with Facebook
  static async signInWithFacebook() {
    try {
      console.log('🔵 Starting Facebook sign-in...');
      
      // This will be called from AuthModal with the Facebook token
      // For now, return an error indicating the token is needed
      // The actual OAuth flow will be handled in AuthModal using react-facebook-login
      return { user: null, error: 'Facebook sign-in should be handled via OAuth component' };
    } catch (error: any) {
      console.error('❌ Facebook sign-in error:', error);
      return { user: null, error: error.message };
    }
  }

  // OAuth login helper (called with provider and token)
  static async oauthLogin(provider: 'google' | 'facebook', token: string) {
    try {
      console.log(`🔵 Starting ${provider} OAuth login...`);
      const result = await ApiService.oauthLogin(provider, token);
      
      if (result.error) {
        return { user: null, error: result.error };
      }
      
      // Update auth state
      if (result.token && result.user) {
        await checkAuthState();
        // Update online status
        await ApiService.updateOnlineStatus(true);
        
        const user: User = {
          uid: result.user.id || result.user.user_id || '',
          email: result.user.email || null,
          displayName: result.user.username || null,
          emailVerified: true,
        };
        
        console.log(`✅ ${provider} OAuth login successful! User ID:`, user.uid);
        return { user, error: null };
      }
      
      return { user: null, error: 'OAuth login failed - no user data returned' };
    } catch (error: any) {
      console.error(`❌ ${provider} OAuth login error:`, error);
      return { user: null, error: error.message };
    }
  }

  // Sign out
  static async signOut() {
    try {
      // Update online status before signing out
      if (currentUser) {
        await ApiService.updateOnlineStatus(false);
      }
      
      await ApiService.signOut();
      updateCurrentUser(null);
      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  }


  // Get current user
  static getCurrentUser(): User | null {
    return currentUser;
  }

  // Record game result
  static async recordGameResult(
    result: 'win' | 'loss' | 'draw',
    opponent?: string,
    playerColor?: 'white' | 'black',
    gameMode?: string,
    moves?: Array<{ row: number; col: number; player: 'white' | 'black'; yugos?: number }>
  ) {
    try {
      if (!currentUser) {
        console.error('❌ No authenticated user for recording game result');
        return { success: false, error: 'No authenticated user' };
      }
      
      // CRITICAL: Online games are recorded server-side ONLY - do NOT record them client-side
      // Only record local games (human vs human on same device) - AI games should NOT count in stats
      // Block online games (server handles them) and AI games
      const isAIgame = gameMode && gameMode.startsWith('ai-');
      const isOnlineGame = gameMode === 'online' || gameMode === 'room';
      
      if (isAIgame) {
        console.log('🚫 AuthService: Not recording stats - AI games do not count');
        return { success: false, error: 'AI games do not count in stats' };
      }
      
      if (isOnlineGame) {
        console.log('🚫 AuthService: Not recording stats - online games are recorded server-side only');
        return { success: false, error: 'Online games are recorded server-side only' };
      }
      
      // Only record local games (human vs human on same device)
      if (gameMode !== 'local') {
        console.log('🚫 AuthService: Not recording stats - unknown game mode:', gameMode);
        return { success: false, error: 'Stats not tracked for this game mode' };
      }
      
      console.log('📊 AuthService: Recording game result', { 
        uid: currentUser.uid, 
        result, 
        opponent, 
        playerColor, 
        gameMode 
      });
      
      // Record game using API service (this handles both stats and history)
      if (opponent && playerColor && gameMode) {
        await ApiService.recordGame(opponent, result, playerColor, gameMode, moves);
        console.log('✅ AuthService: Game result recorded successfully');
      } else {
        console.log('⚠️ AuthService: Missing game details, skipping recording');
      }
      
      return { success: true, error: null };
    } catch (error: any) {
      console.error('❌ AuthService: Error recording game result:', error);
      return { success: false, error: error.message };
    }
  }

  // Get user statistics
  static async getUserStats() {
    try {
      if (!currentUser) {
        throw new Error('No authenticated user');
      }
      
      const result = await ApiService.getUserStats();
      return { stats: result.stats, error: result.error };
    } catch (error: any) {
      return { stats: null, error: error.message };
    }
  }

  // Get leaderboard
  static async getLeaderboard(period: string = 'alltime') {
    try {
      const result = await ApiService.getLeaderboard(period);
      return { leaderboard: result.leaderboard, error: result.error };
    } catch (error: any) {
      return { leaderboard: [], error: error.message };
    }
  }

  // Refresh auth state (useful after email verification, profile updates, etc.)
  static async checkAuthState() {
    return await checkAuthState();
  }
}
